import template from './tbody.pug';
import _findIndex from 'lodash/findIndex';
import _isEmpty from 'lodash/isEmpty';
import _debounce from 'lodash/debounce';
import TbodyActions from '../../tbody-actions';

export const TbodyComponent = {
  template,
  bindings: {
    gridOptions: '<',
    filter: '<',
    checkedIds: '<',
    selectedRecord: '<',
    isVisible: '<',
    isModal: '<?',
    onFilterChange: '&',
    onCheck: '&',
    onActionClick: '&',
    moduleName: '@',
    onSwitch: '&',
    loading: '<',
    onPreviewDirection: '&',
    onPreview: '&',
    onCellEdited: '&',
    onGoTo: '&?',
    onCheckToggle: '&?'
  },
  controller: class TbodyComponent extends TbodyActions {
    constructor($stateParams, $rootScope, $localStorage, $timeout, TableEdit, Signal, Table, Modal, $scope, Hotkeys, uiGridConstants) {
      'ngInject';
      super();
      Object.assign(this, { $stateParams, $rootScope, $localStorage, $timeout, TableEdit, Signal, Table, Modal, $scope, Hotkeys, uiGridConstants });
      this.disableRowClick = false;
      this.initializeScopeMethods();
    }

    initializeScopeMethods() {
      this.$scope.onCellHover = e => this.toggleCellDisplay(e, true);
      this.$scope.onCellOut = e => this.toggleCellDisplay(e, false);
      this.$scope.onCellEdit = e => this.triggerCellEdit(e);
      this.$scope.onCellClose = e => this.handleCellClose(e);
      this.$scope.preview = row => this.previewRow(row);
      this.$scope.openInNewTab = row => this.openRowInNewTab(row);
      this.$scope.onActionClick = (row, action) => this.onActionClick({ $event: { row, action } });
      this.$scope.saveState = () => this.saveGridState();
      this.$scope.restoreState = () => this.restoreGridState();
      this.$scope.goTo = row => this.goToRow(row);
      this.applyFilter = _debounce(() => this.triggerFilterChange(), 350, { maxWait: 1000 });
    }

    toggleCellDisplay(e, display) {
      const elm = e.currentTarget.lastChild;
      angular.element(elm).css('display', display ? 'block' : 'none');
    }

    triggerCellEdit(e) {
      this.disableRowClick = true;
      const event = new MouseEvent('dblclick', { view: window, bubbles: true, cancelable: false });
      e.currentTarget.parentNode.parentNode.focus();
      e.currentTarget.dispatchEvent(event);
    }

    handleCellClose(e) {
      e.preventDefault();
      const row = this.$rootScope.gridApi[this.gridScopeName].grid.rows.find(row => this.savedCell && row.entity.$$hashKey === this.savedCell.entityHashKey);
      if (!row) return;
      row.entity[this.savedCell.field] = this.savedCell.value;
      document.activeElement.blur();
      e.stopPropagation();
    }

    previewRow(row) {
      this.highlightRow(row.entity.id);
      this.onPreview({ $event: { row } });
    }

    openRowInNewTab(row) {
      window.open(`/${this.moduleName}/show/${row.entity.id}`, '_blank');
    }

    saveGridState() {
      if (!this.$localStorage.custom_table_state) this.$localStorage.custom_table_state = {};
      this.$localStorage.custom_table_state[this.gridScopeName] = this.$rootScope.gridApi[this.gridScopeName].saveState.save();
    }

    restoreGridState() {
      if (this.$localStorage.custom_table_state && this.$localStorage.custom_table_state[this.gridScopeName]) {
        this.$rootScope.gridApi[this.gridScopeName].saveState.restore(this.$scope, this.$localStorage.custom_table_state[this.gridScopeName]);
      }
    }

    goToRow(row) {
      if (this.disableRowClick || !this.onGoTo) return;
      this.highlightRow(row.entity.id);
      if (this.isModal) {
        this.$rootScope.gridApi[this.gridScopeName].selection.toggleRowSelection(row);
      } else {
        this.onGoTo({ $event: { row } });
      }
    }

    triggerFilterChange() {
      this.onFilterChange({ $event: { filter: this.filter, searchId: this.$stateParams.searchId } });
    }

    $onChanges({ loading }) {
      if (loading && loading.currentValue) {
        this.$state.reload();
      }
    }

    $onInit() {
      this.gridScopeName = `${this.moduleName}-${this.isModal ? 'modal' : 'table'}`;

      this.Signal.listen('onFilterColumnReset', this.$rootScope, (column_name) => {
        this.clearColumnFilter(column_name);
      });

      this.filter = this.filter || {};
      this.noObjectExist = false;
      this.initGrid();
      this.registerGridApiEvents();
    }

    clearColumnFilter(column_name) {
      const column = this.gridOptions.columnDefs.find(el => el.field === column_name);
      if (!column) return; // column not found

      if (column.filters) {
        column.filters.forEach(filter => {
          filter.term = undefined;
        });
      } else {
        column.filter.term = undefined;
      }
      this.handleFilterChanged(column);
    }

    initGrid() {
      this.Table.activeFilters.loading = true;
      this.loading = true;
      const filters = this.Table.activeFilters[this.gridScopeName];
      const sort = this.Table.activeSort[this.gridScopeName];
      this.setupGridSorting(sort);
      this.applyFilters(filters);
    }

    setupGridSorting(sort) {
      if (sort && sort.sort_to) {
        this.filter = { ...this.filter, ...sort };
        const index = this.gridOptions.columnDefs.findIndex(el => el.field === sort.sort_by);
        if (index !== -1) {
          this.gridOptions.columnDefs[index].sort = { direction: sort.sort_to.toLowerCase() };
        }
      } else {
        const index = this.gridOptions.columnDefs.findIndex(el => el.field === 'created_at');
        if (index !== -1) {
          this.gridOptions.columnDefs[index].sort = { direction: 'desc' };
          this.filter = { ...this.filter, sort_by: 'created_at', sort_to: 'DESC' };
        }
      }
    }

    applyFilters(filters) {
      if (filters) {
        Object.keys(filters).forEach(e => {
          const index = this.gridOptions.columnDefs.findIndex(el => el.field === e);
          if (!_isEmpty(filters[e].filter) || !_isEmpty(filters[e].filters)) {
            this.gridOptions.columnDefs[index] = { ...this.gridOptions.columnDefs[index], ...filters[e] };
          }
        });
      }
      this.resolveFilters(this.gridOptions.columnDefs);
    }

    resolveFilters(columns) {
      columns.forEach(column => {
        let currentFilter;
        if (column.filter || column.filters) {
          this.processSingleFieldFilter(column, currentFilter);
          this.processRangeFilter(column, currentFilter);
        }
        this.Table.activeFilters.loading = false;
      });
      if (this.filter.custom_fields && !this.filter.custom_fields.length) delete this.filter.custom_fields;
      this.applyFilter();
    }

    processSingleFieldFilter(column, currentFilter) {
      const isCustom = column.field.startsWith('c_f_');
      if (!_isEmpty(column.filter) && _isEmpty(column.filters)) {
        if (!_isEmpty(column.filter.term) || typeof column.filter.term === 'number') {
          if (isCustom) {
            this.addCustomFieldFilter(column, currentFilter);
          } else {
            this.filter[column.filter.name] = column.filter.term;
          }
        } else {
          this.removeCustomFieldFilter(column, isCustom);
        }
      }
    }

    addCustomFieldFilter(column, currentFilter) {
      this.filter.custom_fields = this.filter.custom_fields || [];
      currentFilter = this.filter.custom_fields.find(el => el.custom_field_key === column.field_key);
      if (!currentFilter) {
        this.filter.custom_fields.push(this.TableEdit.getCfFillterByCollumn(column));
      } else {
        this.filter.custom_fields[this.filter.custom_fields.indexOf(currentFilter)] = this.TableEdit.getCfFillterByCollumn(column);
      }
    }

    removeCustomFieldFilter(column, isCustom) {
      if (isCustom && this.filter.custom_fields && this.filter.custom_fields.length) {
        this.filter.custom_fields = this.filter.custom_fields.filter(el => el.custom_field_key !== column.field_key);
        if (!this.filter.custom_fields.length) delete this.filter.custom_fields;
      } else {
        delete this.filter[column.filter.name];
      }
    }

    processRangeFilter(column, currentFilter) {
      const isCustom = column.field.startsWith('c_f_');
      if (!_isEmpty(column.filters)) {
        if (isCustom) {
          this.addCustomFieldFilter(column, currentFilter);
        } else {
          column.filters.forEach(elm => {
            if (elm.term) {
              this.filter[elm.name] = elm.term;
            } else {
              delete this.filter[elm.name];
            }
          });
        }
        if (this.filter.custom_fields) {
          this.filter.custom_fields = this.filter.custom_fields.filter(el => el !== undefined);
        }
      }
    }

    registerGridApiEvents() {
      this.gridOptions.onRegisterApi = gridApi => {
        this.$rootScope.gridApi = this.$rootScope.gridApi || {};
        this.$rootScope.gridApi[this.gridScopeName] = gridApi;
        this.Table.setGridApi(this.$rootScope.gridApi);
        this.$rootScope.gridApi[this.gridScopeName].grid.modifyRows(this.gridOptions.data);
        this.setupGridApiEventListeners();
        setTimeout(() => this.$scope.restoreState());
      };
    }

    setupGridApiEventListeners() {
      const gridApi = this.$rootScope.gridApi[this.gridScopeName];
      gridApi.core.on.rowsRendered(this.$scope, () => this.handleRowsRendered());
      gridApi.edit.on.afterCellEdit(this.$scope, (rowEntity, colDef, newValue, oldValue) => this.handleAfterCellEdit(rowEntity, colDef, newValue, oldValue));
      gridApi.colResizable.on.columnSizeChanged(this.$scope, () => this.$scope.saveState());
      gridApi.colMovable.on.columnPositionChanged(this.$scope, () => this.$scope.saveState());
      gridApi.edit.on.cancelCellEdit(this.$scope, () => this.handleCellEditCancel());
      gridApi.edit.on.beginCellEdit(this.$scope, (rowEntity, colDef) => this.handleBeginCellEdit(rowEntity, colDef));
      gridApi.core.on.filterChanged(this.$scope, column => this.handleFilterChanged(column));
      gridApi.core.on.sortChanged(this.$scope, (grid, sortColumns) => this.handleSortChanged(sortColumns));
      gridApi.selection.on.rowSelectionChanged(this.$scope, row => this.toggleCheck(row));
      gridApi.selection.on.rowSelectionChangedBatch(this.$scope, rows => this.toggleMultiCheck(rows));
    }

    handleRowsRendered() {
      if (!this.checkedIds) return;
      this.checkedIds.forEach(id => {
        const checkedRow = this.gridOptions.data.find(e => e.id === id);
        if (checkedRow) {
          this.$rootScope.gridApi[this.gridScopeName].selection.selectRow(checkedRow);
        }
      });
    }

    handleAfterCellEdit(rowEntity, colDef, newValue, oldValue) {
      if (oldValue === newValue) return;
      const index = this.gridOptions.data.findIndex(e => e.id === rowEntity.id);
      this.onCellEdited({ $event: { id: rowEntity.id, index, [colDef.field]: newValue } });
      this.$timeout(() => this.disableRowClick = false, 800);
    }

    handleCellEditCancel() {
      this.savedCell = null;
      this.disableRowClick = false;
    }

    handleBeginCellEdit(rowEntity, colDef) {
      this.disableRowClick = true;
      this.savedCell = { entityHashKey: rowEntity.$$hashKey, field: colDef.field, value: rowEntity[colDef.field] };
    }

    handleFilterChanged(column) {
      let filter = {};
      const isMulty = _isEmpty(column.filter);
      const isDefined = this.Table.activeFilters[this.gridScopeName] && this.Table.activeFilters[this.gridScopeName][column.field];

      if (!isMulty && column.filter && (column.filter.term || typeof column.filter.term === 'number')) {
        filter = { [column.field]: { filter: column.filter } };
      } else if (!isMulty && isDefined) {
        delete this.Table.activeFilters[this.gridScopeName][column.field];
      }

      if (isMulty && column.filters) {
        const hasValidFilters = column.filters.some(filter => filter.term || typeof filter.term === 'number');

        if (hasValidFilters) {
          filter = { [column.field]: { filters: column.filters } };
        } else if (isDefined) {
          delete this.Table.activeFilters[this.gridScopeName][column.field];
        }
      }

      this.Table.setActiveFilters(this.gridScopeName, {
        ...this.Table.activeFilters[this.gridScopeName],
        ...filter
      });

      this.gridOptions.paginationCurrentPage = 1;
      this.filter.page = this.gridOptions.paginationCurrentPage;
      this.resolveFilters(this.gridOptions.columnDefs);
    }

    handleSortChanged(sortColumns) {
      this.gridOptions.paginationCurrentPage = 1;
      this.filter.page = this.gridOptions.paginationCurrentPage;
      if (sortColumns.length) {
        this.filter.sort_by = sortColumns[0].field;
        this.filter.sort_to = sortColumns[0].sort.direction.toUpperCase();
        this.Table.setActiveSort(this.gridScopeName, { sort_by: this.filter.sort_by, sort_to: this.filter.sort_to });
      } else {
        this.Table.setActiveSort(this.gridScopeName, null);
        delete this.filter.sort_to;
        delete this.filter.sort_by;
      }
      this.applyFilter();
      this.$rootScope.gridApi[this.gridScopeName].core.notifyDataChange(this.uiGridConstants.dataChange.COLUMN);
    }

    highlightRow(id) {
      this.gridOptions.data.forEach(row => row.preview = id === row.id);
    }

    previewFowardBack(direction) {
      if (this.$stateParams.stopKeyBinding) return;
      if (!this.isVisible && !this.selectedRecord) return;
      const record = this.selectedRecord;
      let index = _findIndex(this.gridOptions.data, o => o.id === record.id);
      let next_index = direction === 'left' ? index - 1 : index + 1;
      if (next_index === this.gridOptions.data.length) next_index = 0;
      if (next_index < 0) next_index = this.gridOptions.data.length - 1;
      const id = this.gridOptions.data[next_index].id;
      this.onPreviewDirection({ $event: { id } });
    }

    toggleRow(row) {
      row.isSelected = !row.isSelected;
      this.toggleCheck(row);
      if (this.onCheck) this.onCheck({ $event: { selected: row.isSelected } });
    }

    switch(value, id) {
      this.onSwitch({ $event: { value, id } });
    }
  }
};
